import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, MessageCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const response = await axios.get(`${API}/site/contact-info`);
      setContactInfo(response.data);
    } catch (error) {
      console.error('Error fetching contact info:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast({
        title: "Message sent!",
        description: "We'll get back to you soon.",
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <Toaster />

      <div className="bg-black text-white py-6 sm:py-8 md:py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">Contact Us</h1>
          <p className="text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">We'd love to hear from you</p>
        </div>
      </div>

      <div className="container mx-auto px-3 sm:px-4 py-8 sm:py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6">Send us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-1.5 sm:mb-2 text-sm sm:text-base">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm sm:text-base"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-1.5 sm:mb-2 text-sm sm:text-base">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm sm:text-base"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-1.5 sm:mb-2 text-sm sm:text-base">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm sm:text-base"
                  placeholder="Your phone number"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-1.5 sm:mb-2 text-sm sm:text-base">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm sm:text-base"
                  placeholder="Write your message here..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 text-black font-bold py-3 sm:py-4 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 text-sm sm:text-base"
              >
                <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>{loading ? 'Sending...' : 'Send Message'}</span>
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow-lg p-4 sm:p-6 md:p-8 text-black">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6">Get in Touch</h2>
              <p className="mb-4 sm:mb-6 text-black/80 text-sm sm:text-base">
                Have questions about our watches? Want to know more about a specific product? Feel free to reach out!
              </p>

              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="bg-black/10 p-2 sm:p-3 rounded-lg flex-shrink-0">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm sm:text-base md:text-lg mb-0.5 sm:mb-1">Visit Our Store</h3>
                    <p className="text-black/80 text-xs sm:text-sm">{contactInfo?.address || 'Jain Watch Company, Main Market, New Delhi, India - 110001'}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="bg-black/10 p-2 sm:p-3 rounded-lg flex-shrink-0">
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm sm:text-base md:text-lg mb-0.5 sm:mb-1">Call Us</h3>
                    <a href={`tel:${contactInfo?.phone?.replace(/[^0-9+]/g, '') || '9927058909'}`} className="text-black/80 hover:text-black font-semibold text-sm sm:text-base">
                      {contactInfo?.phone || '+91 9927058909'}
                    </a>
                    <p className="text-[10px] sm:text-xs md:text-sm text-black/70 mt-0.5 sm:mt-1">
                      Tue-Sun: {contactInfo?.store_hours?.regular_time_open || '9:00 AM'} - {contactInfo?.store_hours?.regular_time_close || '8:00 PM'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="bg-black/10 p-2 sm:p-3 rounded-lg flex-shrink-0">
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm sm:text-base md:text-lg mb-0.5 sm:mb-1">Email Us</h3>
                    <a href={`mailto:${contactInfo?.email || 'jainwatchcompany1975@gmail.com'}`} className="text-black/80 hover:text-black font-semibold text-xs sm:text-sm break-all">
                      {contactInfo?.email || 'jainwatchcompany1975@gmail.com'}
                    </a>
                    <p className="text-[10px] sm:text-xs md:text-sm text-black/70 mt-0.5 sm:mt-1">We'll respond within 24 hours</p>
                  </div>
                </div>

                {contactInfo?.whatsapp && (
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="bg-black/10 p-2 sm:p-3 rounded-lg flex-shrink-0">
                      <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm sm:text-base md:text-lg mb-0.5 sm:mb-1">WhatsApp</h3>
                      <a 
                        href={/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
                          ? `https://wa.me/${contactInfo.whatsapp}` 
                          : `https://web.whatsapp.com/send?phone=${contactInfo.whatsapp}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-black/80 hover:text-black font-semibold text-sm sm:text-base"
                      >
                        Chat with us on WhatsApp
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Store Hours */}
            <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4">Store Hours</h3>
              
              {/* Special Notice Banner */}
              {contactInfo?.store_hours?.show_notice && contactInfo?.store_hours?.special_notice && (
                <div className="bg-yellow-100 border-l-4 border-yellow-500 p-3 mb-4 rounded">
                  <p className="text-yellow-800 text-sm sm:text-base font-medium">
                    {contactInfo.store_hours.special_notice}
                  </p>
                </div>
              )}
              
              <div className="space-y-2 text-gray-700">
                {/* Regular Days (Tuesday - Sunday) */}
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="font-semibold">Tuesday - Sunday</span>
                  <span>
                    {contactInfo?.store_hours?.regular_time_open || '9:00 AM'} - {contactInfo?.store_hours?.regular_time_close || '8:00 PM'}
                  </span>
                </div>
                
                {/* Monday */}
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="font-semibold">Monday</span>
                  <span>
                    {contactInfo?.store_hours?.monday_status === 'closed' && (
                      <span className="text-red-600">{contactInfo?.store_hours?.monday_note || 'Closed'}</span>
                    )}
                    {contactInfo?.store_hours?.monday_status === 'open' && (
                      <span>{contactInfo?.store_hours?.monday_time_open || '9:00 AM'} - {contactInfo?.store_hours?.monday_time_close || '8:00 PM'}</span>
                    )}
                    {contactInfo?.store_hours?.monday_status === 'varies' && (
                      <span className="text-orange-600">{contactInfo?.store_hours?.monday_note || 'Hours may vary'}</span>
                    )}
                    {!contactInfo?.store_hours?.monday_status && (
                      <span className="text-red-600">Usually Closed</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;
